import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { format, subYears } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  Box,
  ELinxHistoryItem,
  withStyles,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  PaymentOption,
  formattedCurrency,
  sanitizeToNumeric,
  Skeleton,
} from 'vopay';
import { CopyButton, TransactionStatus, Button, Truncate } from 'UI';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import Complete from '@/components/UI/Complete/Complete';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import { useToast } from '@/providers/Toast.provider';
import styles from './ElinxConfirmationPage.module.scss';

const cn = withStyles(styles);

type ElinxType = 'pay' | 'collect' | 'connect' | 'flex';
type NotificationMethod = 'email' | 'sms' | 'none';

const ElinxConfirmationPage: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { eLinxId, type, notificationMethod, eFlexOption } = useParams<{
    eLinxId: string;
    type: ElinxType;
    notificationMethod: NotificationMethod;
    eFlexOption: PaymentOption | string;
  }>();
  const [eLinx, setELinx] = useState<ELinxHistoryItem>();
  const [eLixResponse, , , fetchElinxHistory] = useFetchELinxHistory({});
  const toast = useToast();
  // The eLinx placeholder's own transaction — fetched generically just to read ParentTransactionID.
  const {
    transactions: elinxTransactions,
    fetchTransactionsHistory: fetchElinxTransaction,
    fetching: fetchingElinxTransaction,
  } = useFetchTransactionsHistory();
  // Separate instance so fetching the funding parent doesn't clobber elinxTransactions above.
  const {
    transactions: parentTransactions,
    fetchTransactionsHistory: fetchParentTransaction,
    fetching: fetchingParentTransaction,
  } = useFetchTransactionsHistory();
  const { fundingOptions, fetchFundingOptions, fetching: fetchingFundingOptions } = useFetchFundingOptions();

  // Parse query params
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isScheduled = queryParams.get('scheduled') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchElinxHistory({ ELinxRequestID: eLinxId });

        if (!res.Success) {
          throw new Error(res.ErrorMessage);
        }
        setELinx(res.ELinxRequests[0]);
      } catch (error) {
        console.error(error);
        alertDialog.error(
          t('ErrorMessages.error', { defaultValue: 'Error' }),
          t('ErrorMessages.fetchingError', { defaultValue: 'An error occurred while fetching eLinx history' }),
        );
      }
    };

    fetchData();
  }, [eLinxId]);

  // Funded From / Funding Fee (ePay only)
  useEffect(() => {
    if (type !== 'pay' || !eLinx?.TransactionID) return;

    fetchElinxTransaction({
      StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
      EndDateTime: formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd'),
      TransactionID: eLinx.TransactionID,
    }).catch(() => {
      toast.neutral(t('Confirmation.fetchFundingParentError', { defaultValue: 'Error fetching funding information' }));
    });
  }, [type, eLinx?.TransactionID]);

  const elinxPlaceholderTransaction = useMemo(
    () => elinxTransactions.find((transaction) => transaction.TransactionID === eLinx?.TransactionID),
    [elinxTransactions, eLinx?.TransactionID],
  );

  const isFunded = !!elinxPlaceholderTransaction?.ParentTransactionID;
  // isFunded reads false transiently until the placeholder transaction fetch resolves.
  const isFundingStatusUnknown =
    type === 'pay' && !!eLinx?.TransactionID && !elinxPlaceholderTransaction && fetchingElinxTransaction;

  useEffect(() => {
    const parentTransactionId = elinxPlaceholderTransaction?.ParentTransactionID;
    if (!parentTransactionId) return;

    const loadFundingParent = async () => {
      try {
        await Promise.all([
          fetchParentTransaction({
            StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
            EndDateTime: formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd'),
            TransactionID: parentTransactionId,
          }),
          fetchFundingOptions({ Currency: eLinx?.Currency || 'CAD' }),
        ]);
      } catch {
        toast.neutral(
          t('Confirmation.fetchFundingParentError', { defaultValue: 'Error fetching funding information' }),
        );
      }
    };

    loadFundingParent();
  }, [elinxPlaceholderTransaction?.ParentTransactionID, eLinx?.Currency]);

  const fundingParentTransaction = useMemo(
    () =>
      parentTransactions.find(
        (transaction) => transaction.TransactionID === elinxPlaceholderTransaction?.ParentTransactionID,
      ),
    [parentTransactions, elinxPlaceholderTransaction?.ParentTransactionID],
  );

  const fundedFrom = useMemo(() => {
    if (!fundingParentTransaction) return undefined;

    if (fundingParentTransaction.TransactionType === 'Credit Card') {
      const creditOption = fundingOptions.find((option) => option.Source === 'credit');
      return t('Confirmation.FundedFrom.creditCard', {
        cardNumber: creditOption?.CreditCardNumber || '',
        defaultValue: 'Credit Card {{cardNumber}}',
      });
    }

    if (
      fundingParentTransaction.TransactionType === 'EFT Funding' ||
      fundingParentTransaction.TransactionType === 'ACH Funding'
    ) {
      const bankOption = fundingOptions.find((option) => option.Source === 'bank');
      return t('Confirmation.FundedFrom.bankAccount', {
        accountNumber: bankOption?.AccountNumber || '',
        defaultValue: 'Bank Account {{accountNumber}}',
      });
    }

    return undefined;
  }, [fundingParentTransaction, fundingOptions, t]);

  const fundingFee = useMemo(() => {
    if (!fundingParentTransaction) return undefined;

    // Credit card funding charges its convenience fee as part of the funding transaction itself,
    // so it's on ConvenienceFeeAmount. Bank (EFT/ACH) funding instead bills its fee as a separate
    // sibling Fee transaction (VIOBillingManager::createFeeTransaction) — ConvenienceFeeAmount is
    // never populated there, so derive the fee from the account's funding-option config instead
    // (same billing-subscription rates the backend used to create that Fee transaction).
    if (fundingParentTransaction.TransactionType === 'Credit Card') {
      const fee = Number(sanitizeToNumeric(fundingParentTransaction.ConvenienceFeeAmount) || 0);
      if (fee <= 0) return undefined;
      return `${fundingParentTransaction.Currency} ${formattedCurrency(fundingParentTransaction.ConvenienceFeeAmount)}`;
    }

    if (
      fundingParentTransaction.TransactionType === 'EFT Funding' ||
      fundingParentTransaction.TransactionType === 'ACH Funding'
    ) {
      const bankOption = fundingOptions.find((option) => option.Source === 'bank');
      if (!bankOption) return undefined;
      const feePercent = Number(bankOption.FeePercent || 0);
      const feePerTransaction = Number(sanitizeToNumeric(bankOption.FeePerTransaction) || 0);
      const amount = Number(sanitizeToNumeric(fundingParentTransaction.CreditAmount) || 0);
      const fee = feePerTransaction + (amount * feePercent) / 100;
      if (fee <= 0) return undefined;
      return `${fundingParentTransaction.Currency} ${formattedCurrency(String(fee))}`;
    }

    return undefined;
  }, [fundingParentTransaction, fundingOptions]);

  // A funding parent exists but its details haven't loaded yet — show a skeleton. Never true for
  // a balance-funded request (no ParentTransactionID).
  const isFetchingFundingInfo =
    type === 'pay' &&
    !!elinxPlaceholderTransaction?.ParentTransactionID &&
    !fundingParentTransaction &&
    (fetchingParentTransaction || fetchingFundingOptions);

  const eFlexOptionMap: Record<PaymentOption, string> = {
    multiple: t('Confirmation.multiple', { defaultValue: 'Multiple Payment Methods' }),
    partial: t('Confirmation.partial', { defaultValue: 'Partial Payment' }),
    installments: t('Confirmation.installments', { defaultValue: 'Installment Plan' }),
  };

  const formattedEflexOptions = eFlexOption
    .split(',')
    .filter((opt) => opt === 'multiple' || opt === 'partial' || opt === 'installments')
    .map((opt) => eFlexOptionMap[opt])
    .join(', ');

  const notificationMethodMap: Record<NotificationMethod, string> = {
    sms: t('Confirmation.sms', { defaultValue: 'SMS' }),
    email: t('Confirmation.email', { defaultValue: 'Email' }),
    none: '',
  };

  const elinxTypeMap: Record<ElinxType, string> = {
    pay: t('Type.ePay', { defaultValue: 'ePay' }),
    collect: t('Type.eCollect', { defaultValue: 'eCollect' }),
    connect: t('Type.eConnect', { defaultValue: 'eConnect' }),
    flex: t('Type.eFlex', { defaultValue: 'eFlex' }),
  };

  const descriptionText = useMemo(() => {
    if (type === 'connect') return null;

    const paymentProcessingDescription = t('Confirmation.paymentProcessingDescription', {
      defaultValue: 'Payment will start processing after this contact provides their preferred payment method.',
    });

    if (type === 'pay') {
      const hasApprovalWorkflow = eLinx?.Status === 'pending approval';

      // Avoid asserting a variant before isFunded is known — the closing sentence alone is safe.
      if (isFundingStatusUnknown) {
        return paymentProcessingDescription;
      }

      if (isFunded && hasApprovalWorkflow) {
        return `${t('Confirmation.readyToUseFundedApproval', {
          defaultValue: 'The payment link will be ready to use once this request is approved and the funding settles.',
        })} ${paymentProcessingDescription}`;
      }
      if (isFunded) {
        return `${t('Confirmation.readyToUseFunded', {
          defaultValue: 'The payment link will be ready to use once the funding settles.',
        })} ${paymentProcessingDescription}`;
      }
      if (hasApprovalWorkflow) {
        return `${t('Confirmation.readyToUseApprovalOnly', {
          defaultValue: 'The payment link will be ready to use once this request is approved.',
        })} ${paymentProcessingDescription}`;
      }
      return paymentProcessingDescription;
    }

    if (type === 'collect' || type === 'flex') {
      return t('Confirmation.collectionProcessingDescription', {
        defaultValue: 'Collection will start processing after this contact provides their preferred payment method.',
      });
    }
    return null;
  }, [type, t, isFunded, isFundingStatusUnknown, eLinx?.Status]);

  const title = useMemo(() => {
    if (type === 'pay') {
      return t('Confirmation.titlePay', { defaultValue: 'ePay Request Created' });
    }
    const typeLabel = elinxTypeMap[type];
    return t('Confirmation.title', { type: typeLabel, defaultValue: `${typeLabel} Request Submitted` });
  }, [type, t, elinxTypeMap]);

  const actionText = useMemo(() => {
    if (isScheduled) {
      return t('Confirmation.upcomingAmount', { defaultValue: 'Upcoming Amount' });
    }
    return type === 'pay'
      ? t('Confirmation.paymentAmount', { defaultValue: 'Payment Amount' })
      : t('Confirmation.collectionAmount', { defaultValue: 'Collection Amount' });
  }, [isScheduled, type, t]);

  const recipientText = useMemo(() => {
    if (type === 'connect') return null;

    let translationKey: string;
    let defaultValue: string;

    if (isScheduled) {
      translationKey = type === 'pay' ? 'ElinxHistory.Confirmation.to' : 'ElinxHistory.Confirmation.from';
      defaultValue = type === 'pay' ? 'to <bold>{{name}}</bold>' : 'from <bold>{{name}}</bold>';
    } else {
      translationKey = type === 'pay' ? 'ElinxHistory.Confirmation.payableTo' : 'ElinxHistory.Confirmation.collectFrom';
      defaultValue = type === 'pay' ? 'payable to <bold>{{name}}</bold>' : 'collect from <bold>{{name}}</bold>';
    }

    return (
      <Trans
        ns="translations"
        i18nKey={translationKey}
        defaults={defaultValue}
        values={{ name: eLinx?.ReceiverName }}
        components={{ bold: <strong style={{ color: '#2d2d2d' }} /> }}
      />
    );
  }, [isScheduled, type, eLinx?.ReceiverName, t]);

  const details = useMemo(() => {
    const baseDetails = [
      {
        label: t('Confirmation.elinxLink', { defaultValue: 'eLinx Link' }),
        value: (
          <Box gapSize="8px" ai="center" jc="fe">
            <span>
              <Truncate text={eLinx?.Link || ''} maxLength={25} truncateEnding="..." />
            </span>
            <CopyButton content={eLinx?.Link || ''} />
          </Box>
        ),
      },
      {
        label: t('Confirmation.elinxId', { defaultValue: 'eLinx ID' }),
        value: (
          <Box gapSize="8px" ai="center" jc="fe">
            <span>{eLinx?.ELinxRequestID}</span>
            <CopyButton content={eLinx?.ELinxRequestID || ''} />
          </Box>
        ),
      },
      {
        label: t('Confirmation.status', { defaultValue: 'Status' }),
        value: (
          <Box jc="fe">
            <TransactionStatus
              status={GetColorStatus(eLinx?.Status)}
              label={tGlobal(`Statuses.${eLinx?.Status?.toLowerCase()}`, {
                defaultValue: capitalizeFirstLetterOfWords(eLinx?.Status || ''),
              })}
            />
          </Box>
        ),
      },
      ...(notificationMethod !== 'none'
        ? [
            {
              label: t('Confirmation.notification', { defaultValue: 'Send Notifications via' }),
              value: notificationMethodMap[notificationMethod],
            },
          ]
        : []),
      ...(eFlexOption !== 'none' && formattedEflexOptions
        ? [
            {
              label: t('Confirmation.eflexOptions', { defaultValue: 'eFlex Options' }),
              value: formattedEflexOptions,
            },
          ]
        : []),
      ...(isFetchingFundingInfo
        ? [
            {
              label: t('Confirmation.fundedFrom', { defaultValue: 'Funded From' }),
              value: <Skeleton width={120} height={16} />,
            },
          ]
        : []),
      ...(fundedFrom && !isFetchingFundingInfo
        ? [
            {
              label: t('Confirmation.fundedFrom', { defaultValue: 'Funded From' }),
              value: fundedFrom,
            },
          ]
        : []),
      ...(fundingFee && !isFetchingFundingInfo
        ? [
            {
              label: t('Confirmation.fundingFee', { defaultValue: 'Funding Fee' }),
              value: fundingFee,
            },
          ]
        : []),
    ];

    return baseDetails;
  }, [
    eLinx,
    notificationMethod,
    notificationMethodMap,
    eFlexOption,
    formattedEflexOptions,
    fundedFrom,
    fundingFee,
    isFetchingFundingInfo,
    t,
    tGlobal,
  ]);

  const buttonLabel = useMemo(() => {
    return t('Confirmation.viewDetails', {
      type: elinxTypeMap[type],
      defaultValue: `View ${elinxTypeMap[type]} Details`,
    });
  }, [type, elinxTypeMap, t]);

  const createNewLabel = useMemo(() => {
    return t('Confirmation.createNew', { type: elinxTypeMap[type], defaultValue: `Create New ${elinxTypeMap[type]}` });
  }, [type, elinxTypeMap, t]);

  const handleViewDetails = () => {
    history.replace(`/elinx-history/details/${eLinx?.ELinxRequestID || ''}`);
  };

  const handleCreateNew = () => {
    history.replace(`/elinx/${type}`);
  };

  if (!eLixResponse.Success || !eLinx) {
    return null;
  }

  // Description to show under the title
  const description =
    type === 'connect' ? (
      <div className={cn('message')}>
        <Trans
          ns="translations"
          i18nKey="ElinxHistory.Confirmation.connectSummary"
          values={{ receiverName: eLinx?.ReceiverName }}
          components={{
            span: <span style={{ fontWeight: 600, color: '#2D2D2D' }} />,
          }}
        />
      </div>
    ) : descriptionText ? (
      descriptionText
    ) : null;

  return (
    <Complete
      title={title}
      amount={type !== 'connect' ? `${eLinx?.Currency} ${formattedCurrency(String(eLinx?.Amount))}` : undefined}
      actionText={type !== 'connect' ? actionText : undefined}
      recipientText={type !== 'connect' ? recipientText : undefined}
      showAmounts={type !== 'connect'}
      details={details}
      description={description}
      className={cn('container')}
    >
      <div className={cn('buttonContainer')}>
        <Button size="small" onClick={handleViewDetails}>
          {buttonLabel}
        </Button>
        <Button size="small" colorScheme="primary" onClick={handleCreateNew}>
          {createNewLabel}
        </Button>
      </div>
    </Complete>
  );
};

export default ElinxConfirmationPage;
